import '../core/load-more-results/load-more-results'
import '../core/loading-button/loading-button'
import { GMXLoadMoreResults } from '../core/load-more-results/load-more-results'
import type { SearchApiResponse } from '../../models/search-api.model'
import { sanitizeInput } from '../../utils/sanitize'

/**
 * Search Results Component
 *
 * Fetches and displays search results from an API endpoint. Uses `<gmx-load-more-results>`
 * internally for progressive disclosure and pagination.
 *
 * ## Integration Patterns
 *
 * This component works with `<gmx-site-search>` in two ways:
 *
 * ### Pattern A: Navigation (Cross-page)
 * User searches on Page A, navigates to Page B where results are displayed.
 *
 * **Page A (e.g., homepage):**
 * ```html
 * <gmx-site-search
 *   data-results-page-url="/site-search"
 *   data-autocomplete-path="/api/autocomplete"
 * ></gmx-site-search>
 * ```
 *
 * **Page B (/site-search):**
 * ```html
 * <gmx-search-results
 *   data-search-api-path="/api/search"
 * ></gmx-search-results>
 * ```
 *
 * When user searches, they navigate to `/site-search?q=query`. This component
 * automatically reads the query from the URL (`?q=`) and fetches results on page load.
 *
 * ### Pattern B: Same-page
 * User searches and results appear on the same page without navigation.
 *
 * ```html
 * <gmx-site-search
 *   data-search-results-id="my-results"
 *   data-autocomplete-path="/api/autocomplete"
 * ></gmx-site-search>
 *
 * <gmx-search-results
 *   id="my-results"
 *   data-search-api-path="/api/search"
 * ></gmx-search-results>
 * ```
 *
 * The `<gmx-site-search>` component finds this element by ID and calls `.search()` directly.
 *
 * ## Data Attributes
 *
 * ### API Configuration
 * - `data-search-api-path` - API endpoint for search results (required)
 * - `data-page-size` - Number of results to fetch per API call (default: 20)
 * - `data-query-param` - Query parameter name for URL reading (default: "q")
 * - `data-start-param` - Start/offset parameter name (default: "start")
 * - `data-num-param` - Number/count parameter name (default: "num")
 *
 * ### Display Configuration
 * - `data-initial-visible` - Items to show initially (default: 10)
 * - `data-load-more-count` - Items to reveal per "load more" click (default: 5)
 *
 * ### i18n Labels
 * - `data-results-title` - Title prefix for results (default: "Results for")
 * - `data-no-results-message` - Message when no results (default: "No results found")
 * - `data-error-message` - Message when API error occurs (default: "There was an issue with your search please try again later")
 * - `data-load-more-label` - Load more button text (default: "Load more")
 * - `data-loading-label` - Loading state text (default: "Loading")
 *
 * ## Methods
 * - `search(query: string)` - Perform a search programmatically
 * - `reset()` - Clear results and reset state
 *
 * ## Events
 * - `search-results-loaded` - Fired when results are loaded (detail: `{ query, results, totalResults, currentStart }`)
 * - `search-results-error` - Fired on API error (detail: `{ query, error }`)
 * - `gbaal:event` - Fired once per `search()` call for GBAAL analytics (never on load-more)
 *
 * @example
 * // Standalone usage
 * <gmx-search-results
 *   data-search-api-path="/bypass/pcf/search/quantum_buick_us_en"
 *   data-page-size="20"
 *   data-initial-visible="10"
 *   data-load-more-count="5"
 * ></gmx-search-results>
 */
export class GMXSearchResults extends HTMLElement {
	private loadMoreResults: GMXLoadMoreResults | null = null
	private resultsContainer: HTMLElement | null = null
	private titleEl: HTMLElement | null = null
	private loadingEl: HTMLElement | null = null
	private noResultsEl: HTMLElement | null = null
	private errorEl: HTMLElement | null = null
	private currentQuery = ''
	private currentStart = 0
	private totalResults = 0
	private isInitialized = false

	connectedCallback(): void {
		if (this.isInitialized) return
		this.isInitialized = true
		this._render()
		this._setupEventListeners()
		this._checkQueryString()
	}

	private _render(): void {
		const loadMoreLabel = this.getAttribute('data-load-more-label') || 'Load more'
		const loadingLabel = this.getAttribute('data-loading-label') || 'Loading'
		const noResultsMessage = this.getAttribute('data-no-results-message') || 'No results found'
		const errorMessage = this.getAttribute('data-error-message') || 'There was an issue with your search please try again later'
		const rawInitialVisible = this.getAttribute('data-initial-visible')
		const rawLoadMoreCount = this.getAttribute('data-load-more-count')

		const safeLoadMoreLabel = sanitizeInput(loadMoreLabel)
		const safeLoadingLabel = sanitizeInput(loadingLabel)
		const safeNoResultsMessage = sanitizeInput(noResultsMessage)
		const safeErrorMessage = sanitizeInput(errorMessage)

		let initialVisibleNum = parseInt(rawInitialVisible || '', 10)
		if (!Number.isFinite(initialVisibleNum) || initialVisibleNum <= 0) {
			initialVisibleNum = 10
		}
		const initialVisible = String(initialVisibleNum)

		let loadMoreCountNum = parseInt(rawLoadMoreCount || '', 10)
		if (!Number.isFinite(loadMoreCountNum) || loadMoreCountNum <= 0) {
			loadMoreCountNum = 5
		}
		const loadMoreCount = String(loadMoreCountNum)

		this.innerHTML = `
			<div class="gmx-search-results">
				<h2 class="gmx-search-results-title gmx-site-results-title" style="display: none;"></h2>
				<div class="gmx-search-results-loading" style="display: none; padding: 32px 0; text-align: center;">
					<gmx-loading-btn data-loading>
						<button type="button" class="gmx-btn gmx-btn-secondary gmx-loading-btn">
							<span class="loading-btn-text">${safeLoadMoreLabel}</span>
							<span class="loading-btn-loading">${safeLoadingLabel}<span class="loading-btn-ellipsis"></span></span>
						</button>
					</gmx-loading-btn>
				</div>
				<div class="gmx-search-results-no-results gmx-site-results-title" style="display: none;">
					<p>${safeNoResultsMessage}</p>
				</div>
				<div class="gmx-search-results-error gmx-site-results-title" style="display: none;">
					<p>${safeErrorMessage}</p>
				</div>
				<gmx-load-more-results
					data-initial-visible="${initialVisible}"
					data-load-more-count="${loadMoreCount}"
					style="display: none;"
				>
					<div class="gmx-load-more-results-container">
						<ul class="gmx-text-list layout-stacked style-title-desc"></ul>
					</div>
					<div class="gmx-load-more-btn-container">
						<gmx-loading-btn>
							<button type="button" class="gmx-btn gmx-btn-secondary gmx-loading-btn">
								<span class="loading-btn-text">${safeLoadMoreLabel}</span>
								<span class="loading-btn-loading">${safeLoadingLabel}<span class="loading-btn-ellipsis"></span></span>
							</button>
						</gmx-loading-btn>
					</div>
				</gmx-load-more-results>
			</div>
		`

		this.titleEl = this.querySelector('.gmx-search-results-title')
		this.loadingEl = this.querySelector('.gmx-search-results-loading')
		this.noResultsEl = this.querySelector('.gmx-search-results-no-results')
		this.errorEl = this.querySelector('.gmx-search-results-error')
		this.loadMoreResults = this.querySelector('gmx-load-more-results')
		this.resultsContainer = this.querySelector('.gmx-load-more-results-container ul')
	}

	private _setupEventListeners(): void {
		this.loadMoreResults?.addEventListener('load-more-requested', this._handleLoadMoreRequested.bind(this))
	}

	private _checkQueryString(): void {
		const urlParams = new URLSearchParams(window.location.search)
		const queryParam = this.getAttribute('data-query-param') || 'q'
		const query = urlParams.get(queryParam)
		if (query && query.trim()) {
			this.search(sanitizeInput(query.trim()))
		}
	}

	private _handleLoadMoreRequested = async (): Promise<void> => {
		// Fetch more results from API if we have more to load
		if (this.currentStart < this.totalResults) {
			await this._fetchResults(this.currentQuery, false)
		}
	}

	/**
	 * Perform a search with the given query
	 */
	async search(query: string): Promise<void> {
		const searchApiPath = this.getAttribute('data-search-api-path')
		if (!searchApiPath) {
			// The visitor performed a search this instance can't service, so report it rather
			// than let a misconfigured deployment read as no search traffic at all.
			this._dispatchGbaalSearchEvent(query, 0)
			return
		}

		this.currentQuery = query
		this.currentStart = 0
		this.totalResults = 0

		// Clear previous results
		if (this.resultsContainer) {
			this.resultsContainer.innerHTML = ''
		}

		// Show loading state
		this._showLoading()

		await this._fetchResults(query, true)
	}

	private async _fetchResults(query: string, isInitialSearch: boolean): Promise<void> {
		const searchApiPath = this.getAttribute('data-search-api-path')
		if (!searchApiPath) return

		const pageSize = parseInt(this.getAttribute('data-page-size') || '20', 10)
		const queryParam = this.getAttribute('data-query-param') || 'q'
		const startParam = this.getAttribute('data-start-param') || 'start'
		const numParam = this.getAttribute('data-num-param') || 'num'

		// Rendering happens inside the same `try`, so a throw after a successful response would
		// otherwise reach the catch and report the search a second time as a failure.
		let hasDispatchedGbaalEvent = false

		try {
			const baseUrl = window.location.origin || ''
			const url = new URL(searchApiPath, baseUrl)
			url.searchParams.set(queryParam, query)
			url.searchParams.set(startParam, String(this.currentStart))
			url.searchParams.set(numParam, String(pageSize))

			const response = await fetch(url.toString())
			if (!response.ok) {
				throw new Error(`HTTP ${response.status}`)
			}

			const data = await response.json() as SearchApiResponse | Record<string, unknown>
			const results = this._extractResults(data)

			// Extract total results based on API format
			if (this._isSearchApiResponse(data)) {
				this.totalResults = data.hits.total
			} else {
				// Legacy formats
				this.totalResults = (data as Record<string, unknown>).totalResults
					? Number((data as Record<string, unknown>).totalResults)
					: (data as Record<string, unknown>).total
						? Number((data as Record<string, unknown>).total)
						: results.length
			}

			if (isInitialSearch) {
				this._hideLoading()
				this._updateTitle(query)
				this._dispatchGbaalSearchEvent(query, this.totalResults)
				hasDispatchedGbaalEvent = true
			}

			if (results.length === 0 && isInitialSearch) {
				this._showNoResults()
				return
			}

			this._appendResults(results)
			this.currentStart += results.length

			if (isInitialSearch) {
				this._showResults()
			}

			this.dispatchEvent(new CustomEvent('search-results-loaded', {
				bubbles: true,
				composed: true,
				detail: { query, results, totalResults: this.totalResults, currentStart: this.currentStart }
			}))

		} catch (error) {
			this._hideLoading()
			if (isInitialSearch) {
				this._showError()
				if (!hasDispatchedGbaalEvent) {
					this._dispatchGbaalSearchEvent(query, 0)
				}
			}
			this.dispatchEvent(new CustomEvent('search-results-error', {
				bubbles: true,
				composed: true,
				detail: { query, error }
			}))
		}
	}

	/**
	 * Emit a fire-and-forget GBAAL analytics event describing a site search the visitor performed.
	 *
	 * `search` is a GBAAL push method, so naming it exactly gets the payload full page/user/
	 * platform/consent/session enrichment, `eventData` tagging, and schema validation; any other
	 * name silently falls through to GBAAL's generic custom-event path with none of that. When
	 * GBAAL isn't loaded the event is a no-op — there is no hard dependency on `window.gbaal`.
	 * `composed: true` is required so the event escapes Shadow DOM boundaries of nested custom
	 * elements.
	 *
	 * This component is the single reporter for site search because it is the only one that knows
	 * `resultCount`; every path that finishes a visitor-initiated search funnels through here
	 * exactly once, while load-more pagination never does.
	 *
	 * @param query - The search term entered by the user.
	 * @param resultCount - Total results the search returned; 0 when the search could not run.
	 * @private
	 */
	private _dispatchGbaalSearchEvent(query: string, resultCount: number): void {
		const search = {
			term: (query ?? '').toLowerCase(),
			type: 'site search',
			resultCount,
		}

		const detail = { event: 'search', payload: { search } }
		this.dispatchEvent(new CustomEvent('gbaal:event', { detail, bubbles: true, composed: true }))
	}

	private _isSearchApiResponse(data: SearchApiResponse | Record<string, unknown>): data is SearchApiResponse {
		// Check if data matches SearchApiResponse structure
		return (
			'hits' in data &&
			data.hits !== null &&
			typeof data.hits === 'object' &&
			'hits' in data.hits &&
			Array.isArray((data.hits as { hits: unknown }).hits) &&
			'total' in data.hits &&
			typeof (data.hits as { total: unknown }).total === 'number'
		)
	}

	private _extractResults(data: SearchApiResponse | Record<string, unknown>): { title: string; description?: string; url?: string }[] {
		// Check if this matches the new SearchApiResponse format
		if (this._isSearchApiResponse(data)) {
			return data.hits.hits.map(hit => {
				const highlight = hit.highlight

				const title = this._stripHtmlTags(highlight?.title?.[0]) || hit._source?.title || ''
				const description = this._stripHtmlTags(highlight?.description?.[0]) || hit._source?.description
				const url = hit._id

				return { title, description, url }
			}).filter(item => item.title)
		}

		// Fall back to legacy API response formats
		const results = (data as Record<string, unknown>).results
			|| (data as Record<string, unknown>).items
			|| (data as Record<string, unknown>).data
			|| []

		if (!Array.isArray(results)) return []

		return results.map((item: Record<string, unknown>) => ({
			title: String(item.title || item.name || ''),
			description: item.description ? String(item.description) : undefined,
			url: item.url ? String(item.url) : item.link ? String(item.link) : undefined
		})).filter(item => item.title)
	}

	private _stripHtmlTags(html: string | undefined): string {
		if (!html) return ''
		return html.replace(/<[^>]*>/g, '')
	}


	private _appendResults(results: { title: string; description?: string; url?: string }[]): void {
		if (!this.resultsContainer) return

		results.forEach(result => {
			const li = document.createElement('li')
			const anchor = document.createElement('a')
			anchor.className = 'list-btn'
			anchor.href = result.url || '#'

			const titleDiv = document.createElement('div')
			titleDiv.className = 'gmx-list-title'
			titleDiv.textContent = result.title
			anchor.appendChild(titleDiv)

			if (result.description) {
				const descDiv = document.createElement('div')
				descDiv.className = 'gmx-list-desc'
				descDiv.textContent = result.description
				anchor.appendChild(descDiv)
			}

			li.appendChild(anchor)
			this.resultsContainer?.appendChild(li)
		})

		// Refresh load-more-results to recognize new items
		this.loadMoreResults?.refresh()
	}

	private _updateTitle(query: string): void {
		if (!this.titleEl) return
		const titlePrefix = this.getAttribute('data-results-title') || 'Results for'
		this.titleEl.textContent = `${titlePrefix} "${query}"`
		this.titleEl.style.display = ''
	}

	private _showLoading(): void {
		if (this.loadingEl) this.loadingEl.style.display = ''
		if (this.loadMoreResults) this.loadMoreResults.style.display = 'none'
		if (this.noResultsEl) this.noResultsEl.style.display = 'none'
		if (this.errorEl) this.errorEl.style.display = 'none'
		if (this.titleEl) this.titleEl.style.display = 'none'
	}

	private _hideLoading(): void {
		if (this.loadingEl) this.loadingEl.style.display = 'none'
	}

	private _showResults(): void {
		if (this.loadMoreResults) this.loadMoreResults.style.display = ''
		if (this.noResultsEl) this.noResultsEl.style.display = 'none'
		if (this.errorEl) this.errorEl.style.display = 'none'
	}

	private _showNoResults(): void {
		if (this.noResultsEl) this.noResultsEl.style.display = ''
		if (this.loadMoreResults) this.loadMoreResults.style.display = 'none'
		if (this.errorEl) this.errorEl.style.display = 'none'
	}

	private _showError(): void {
		if (this.errorEl) this.errorEl.style.display = ''
		if (this.loadMoreResults) this.loadMoreResults.style.display = 'none'
		if (this.noResultsEl) this.noResultsEl.style.display = 'none'
	}

	/**
	 * Reset the component to initial state
	 */
	reset(): void {
		this.currentQuery = ''
		this.currentStart = 0
		this.totalResults = 0
		if (this.resultsContainer) this.resultsContainer.innerHTML = ''
		if (this.titleEl) this.titleEl.style.display = 'none'
		if (this.loadMoreResults) this.loadMoreResults.style.display = 'none'
		if (this.noResultsEl) this.noResultsEl.style.display = 'none'
		if (this.errorEl) this.errorEl.style.display = 'none'
		if (this.loadingEl) this.loadingEl.style.display = 'none'
		this.loadMoreResults?.reset()
	}
}

if (!customElements.get('gmx-search-results')) {
	customElements.define('gmx-search-results', GMXSearchResults)
}
